import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { PageHeader, GlassCard, PrimaryButton, GhostButton } from "@/components/Primitives";
import Modal, { inputCls, Field } from "@/components/Modal";
import { Plus, X, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

const Pill = ({ children, tone = "info" }) => {
  const tones = {
    error: "bg-rose-400/10 text-rose-300 border-rose-400/20",
    warning: "bg-amber-400/10 text-amber-300 border-amber-400/20",
    info: "bg-sky-400/10 text-sky-300 border-sky-400/20",
    success: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
};

const blankH = { client_id: "", domain: "", hosting_provider: "Vercel", monthly_cost: 0, monthly_charge: 0, renewal_date: "", ssl_expiration: "", status: "active" };
const blankD = { domain: "", registrar: "Namecheap", client_id: "", expiration_date: "", renewal_cost: 0, auto_renew: true };

function HostingDialog({ open, initial, clients, onClose, onSaved }) {
  const [form, setForm] = useState(blankH);
  useEffect(() => {
    if (open) setForm(initial ? {
      ...blankH, ...initial,
      renewal_date: (initial.renewal_date || "").slice(0, 10),
      ssl_expiration: (initial.ssl_expiration || "").slice(0, 10),
    } : blankH);
  }, [open, initial]);

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthly_cost: Number(form.monthly_cost) || 0,
        monthly_charge: Number(form.monthly_charge) || 0,
        renewal_date: form.renewal_date || null,
        ssl_expiration: form.ssl_expiration || null,
      };
      if (isEdit) await api.patch(`/hosting/${initial.id}`, payload);
      else await api.post("/hosting", payload);
      toast.success(isEdit ? "Saved" : "Created");
      onClose(); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const confirm = useConfirm();
  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this hosting record?",
      description: `${initial?.domain || "This record"} will be removed and stop counting toward MRR.`,
      confirmLabel: "Delete hosting",
    });
    if (!ok) return;
    try { await api.delete(`/hosting/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <form onSubmit={submit} className="p-8" data-testid="hosting-dialog">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-light">{isEdit ? "Edit hosting" : "New hosting"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Client">
            <select required className={inputCls} value={form.client_id} onChange={u("client_id")}>
              <option value="">— Select —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Field>
          <Field label="Domain"><input required className={inputCls} value={form.domain} onChange={u("domain")} /></Field>
          <Field label="Provider"><input className={inputCls} value={form.hosting_provider} onChange={u("hosting_provider")} /></Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={u("status")}>
              {["active", "paused", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Monthly Cost"><input type="number" className={inputCls} value={form.monthly_cost} onChange={u("monthly_cost")} /></Field>
          <Field label="Monthly Charge"><input type="number" className={inputCls} value={form.monthly_charge} onChange={u("monthly_charge")} /></Field>
          <Field label="Renewal Date"><input type="date" className={inputCls} value={form.renewal_date} onChange={u("renewal_date")} /></Field>
          <Field label="SSL Expiration"><input type="date" className={inputCls} value={form.ssl_expiration} onChange={u("ssl_expiration")} /></Field>
        </div>
        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 text-sm inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit" data-testid="hosting-save">{isEdit ? "Save" : "Create"}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function DomainDialog({ open, initial, clients, onClose, onSaved }) {
  const [form, setForm] = useState(blankD);
  useEffect(() => {
    if (open) setForm(initial ? { ...blankD, ...initial, expiration_date: (initial.expiration_date || "").slice(0, 10) } : blankD);
  }, [open, initial]);

  const u = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        renewal_cost: Number(form.renewal_cost) || 0,
        client_id: form.client_id || null,
      };
      if (isEdit) await api.patch(`/domains/${initial.id}`, payload);
      else await api.post("/domains", payload);
      toast.success(isEdit ? "Saved" : "Created");
      onClose(); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const confirm = useConfirm();
  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this domain?",
      description: `${initial?.domain || "This domain"} will be removed from your registry. Renewal alerts will stop.`,
      confirmLabel: "Delete domain",
    });
    if (!ok) return;
    try { await api.delete(`/domains/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={submit} className="p-8" data-testid="domain-dialog">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-light">{isEdit ? "Edit domain" : "New domain"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Domain"><input required className={inputCls} value={form.domain} onChange={u("domain")} /></Field>
          <Field label="Registrar"><input className={inputCls} value={form.registrar} onChange={u("registrar")} /></Field>
          <Field label="Client">
            <select className={inputCls} value={form.client_id || ""} onChange={u("client_id")}>
              <option value="">— None —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Field>
          <Field label="Expiration"><input required type="date" className={inputCls} value={form.expiration_date} onChange={u("expiration_date")} /></Field>
          <Field label="Renewal Cost"><input type="number" className={inputCls} value={form.renewal_cost} onChange={u("renewal_cost")} /></Field>
          <Field label="Auto Renew">
            <label className="flex items-center gap-2 mt-2.5">
              <input type="checkbox" checked={!!form.auto_renew} onChange={u("auto_renew")} className="accent-[#007AFF] w-4 h-4" />
              <span className="text-sm text-white/70">{form.auto_renew ? "Yes" : "No"}</span>
            </label>
          </Field>
        </div>
        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 text-sm inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit">{isEdit ? "Save" : "Create"}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default function Hosting() {
  const [hosting, setHosting] = useState([]);
  const [domains, setDomains] = useState([]);
  const [clients, setClients] = useState([]);
  const [openH, setOpenH] = useState(false);
  const [editH, setEditH] = useState(null);
  const [openD, setOpenD] = useState(false);
  const [editD, setEditD] = useState(null);

  const load = async () => {
    const [h, d, c] = await Promise.all([api.get("/hosting"), api.get("/domains"), api.get("/clients")]);
    setHosting(h.data); setDomains(d.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const totalRev = hosting.reduce((s, h) => s + (h.monthly_charge || 0), 0);
  const totalProfit = hosting.reduce((s, h) => s + (h.monthly_profit || 0), 0);

  return (
    <div data-testid="hosting-page">
      <PageHeader
        title="Hosting & Domains"
        subtitle="Recurring infrastructure, renewals, and margin."
        testId="hosting-header"
        action={
          <div className="flex gap-3">
            <GhostButton onClick={() => { setEditD(null); setOpenD(true); }} data-testid="new-domain-btn"><Plus className="w-4 h-4" /> Domain</GhostButton>
            <PrimaryButton onClick={() => { setEditH(null); setOpenH(true); }} data-testid="new-hosting-btn"><Plus className="w-4 h-4" /> Hosting</PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Hosting MRR</div><div className="font-display text-3xl">{fmtMoney(totalRev)}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Monthly Profit</div><div className="font-display text-3xl">{fmtMoney(totalProfit)}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Active Sites</div><div className="font-display text-3xl">{hosting.length}</div></GlassCard>
      </div>

      <GlassCard className="mb-8">
        <div className="font-display text-xl mb-4">Hosting</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                <th className="py-3 pr-4">Domain</th>
                <th className="py-3 pr-4">Client</th>
                <th className="py-3 pr-4">Provider</th>
                <th className="py-3 pr-4">Cost</th>
                <th className="py-3 pr-4">Charge</th>
                <th className="py-3 pr-4">Profit/mo</th>
                <th className="py-3 pr-4">Renewal</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {hosting.map((h) => (
                <tr key={h.id} className="hover:bg-white/[0.03]">
                  <td className="py-3 pr-4 text-white">{h.domain}</td>
                  <td className="py-3 pr-4 text-white/60">{h.client_name}</td>
                  <td className="py-3 pr-4 text-white/60">{h.hosting_provider}</td>
                  <td className="py-3 pr-4 text-white/60">{fmtMoney(h.monthly_cost)}</td>
                  <td className="py-3 pr-4 text-white">{fmtMoney(h.monthly_charge)}</td>
                  <td className="py-3 pr-4 text-emerald-300">{fmtMoney(h.monthly_profit)}</td>
                  <td className="py-3 pr-4 text-white/60">{fmtDate(h.renewal_date)}</td>
                  <td className="py-3 pr-4 text-right whitespace-nowrap">
                    <button onClick={() => { setEditH(h); setOpenH(true); }} className="text-white/55 hover:text-white p-1.5" data-testid={`hosting-edit-${h.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {hosting.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-white/40">No hosting records yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </GlassCard>

      <GlassCard>
        <div className="font-display text-xl mb-4">Domains</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                <th className="py-3 pr-4">Domain</th>
                <th className="py-3 pr-4">Registrar</th>
                <th className="py-3 pr-4">Client</th>
                <th className="py-3 pr-4">Expires</th>
                <th className="py-3 pr-4">Days Left</th>
                <th className="py-3 pr-4">Auto</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {domains.map((d) => {
                let tone = "info";
                if (d.days_until_expiration !== null && d.days_until_expiration <= 7) tone = "error";
                else if (d.days_until_expiration !== null && d.days_until_expiration <= 30) tone = "warning";
                return (
                  <tr key={d.id} className="hover:bg-white/[0.03]">
                    <td className="py-3 pr-4 text-white">{d.domain}</td>
                    <td className="py-3 pr-4 text-white/60">{d.registrar}</td>
                    <td className="py-3 pr-4 text-white/60">{d.client_name}</td>
                    <td className="py-3 pr-4 text-white/60">{fmtDate(d.expiration_date)}</td>
                    <td className="py-3 pr-4"><Pill tone={tone}>{d.days_until_expiration} days</Pill></td>
                    <td className="py-3 pr-4 text-white/60">{d.auto_renew ? "Yes" : "No"}</td>
                    <td className="py-3 pr-4 text-right">
                      <button onClick={() => { setEditD(d); setOpenD(true); }} className="text-white/55 hover:text-white p-1.5" data-testid={`domain-edit-${d.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {domains.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-white/40">No domains tracked.</td></tr>}
            </tbody>
          </table>
        </div>
      </GlassCard>

      <HostingDialog open={openH} initial={editH} clients={clients} onClose={() => { setOpenH(false); setEditH(null); }} onSaved={load} />
      <DomainDialog open={openD} initial={editD} clients={clients} onClose={() => { setOpenD(false); setEditD(null); }} onSaved={load} />
    </div>
  );
}
